import { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { chatKeys, generateUUID } from '@codes/chatbots';

import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

export interface ChatViewWithError extends ChatView {
    isError?: boolean;
}

interface UseChatBaseProps {
    chatId: string | undefined;
    messagesData: { pages: Array<{ data?: ChatView[] }> } | undefined;
    limit: number;
}

const sortByOrder = (a: ChatView, b: ChatView) => {
    const aOrder = a.childNo ?? a.no ?? 0;
    const bOrder = b.childNo ?? b.no ?? 0;
    return aOrder - bOrder;
};

export const useChatBase = ({ chatId, messagesData, limit }: UseChatBaseProps) => {
    const queryClient = useQueryClient();
    const [pendingMessage, setPendingMessage] = useState<ChatViewWithError | null>(null);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);
    const [input, setInput] = useState('');
    const messagesLengthRef = useRef(0);

    // Process messages from query data (single pass)
    const processedMessages = useMemo(() => {
        if (!messagesData?.pages) {
            return [];
        }

        const allMessages = messagesData.pages.reduce<ChatView[]>((acc, page) => {
            return [...acc, ...(page.data || [])];
        }, []);

        // Process documentIds from search messages before filtering
        const withDocIds = allMessages.map((msg, index) => {
            if (msg.stereo === 'answer' && !msg.documentIds) {
                for (let i = index - 1; i >= 0; i--) {
                    const prevMessage = allMessages[i];
                    if (prevMessage.stereo === 'search' && prevMessage.documentIds) {
                        return { ...msg, documentIds: prevMessage.documentIds };
                    }
                }
            }
            return msg;
        });

        const result = withDocIds.filter(msg => msg.id && msg.content && !msg.hidden).sort(sortByOrder);

        messagesLengthRef.current = result.length;
        return result;
    }, [messagesData]);

    // Display messages = processed + pending (single sort)
    const displayMessages = useMemo(() => {
        if (!pendingMessage) {
            return processedMessages.filter(chat => chat.stereo === 'query' || chat.stereo === 'answer');
        }
        return [...processedMessages, pendingMessage]
            .filter(chat => !chat.hidden && (chat.stereo === 'query' || chat.stereo === 'answer'))
            .sort(sortByOrder);
    }, [processedMessages, pendingMessage]);

    const clearPendingMessage = useCallback(() => {
        setPendingMessage(null);
        setIsWaitingResponse(false);
    }, []);

    const createPendingMessage = useCallback(
        (messageContent: string): ChatViewWithError | null => {
            if (!messageContent.trim() || !chatId || isWaitingResponse) {
                return null;
            }

            const userMessage: ChatViewWithError = {
                id: generateUUID(),
                content: messageContent.trim(),
                stereo: 'query',
                createdAt: Date.now(),
                childNo: (messagesLengthRef.current + 1) * 10,
            };

            setPendingMessage(userMessage);
            setIsWaitingResponse(true);
            setInput('');

            return userMessage;
        },
        [chatId, isWaitingResponse]
    );

    const markPendingAsError = useCallback(() => {
        setPendingMessage(prev => (prev ? { ...prev, isError: true } : null));
        setIsWaitingResponse(false);
    }, []);

    // Shared cache update for chat messages
    const updateChatMessages = useCallback(
        async (targetChatId: string, newMessages: ChatView[]) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack Query setQueryData callback type not inferrable
            await queryClient.setQueryData(chatKeys.list({ rootId: targetChatId, limit }), (oldData: any) => {
                if (!oldData) {
                    return oldData;
                }

                const newFirstPage = {
                    ...oldData.pages[0],
                    data: [...(oldData.pages[0].data || []), ...newMessages],
                };
                return {
                    ...oldData,
                    pages: [newFirstPage, ...oldData.pages.slice(1)],
                    pageParams: oldData.pageParams,
                };
            });
        },
        [queryClient, limit]
    );

    return {
        displayMessages,
        pendingMessage,
        input,
        setInput,
        isWaitingResponse,
        clearPendingMessage,
        createPendingMessage,
        markPendingAsError,
        updateChatMessages,
    };
};
